import { useState } from "react";

// Iconițe inline (aceleași contururi ca în Hub Client — shield-check /
// log-out din Lucide), fără dependență nouă de pachet, doar ca să
// păstrăm exact aceeași identitate vizuală a suitei.
export function ShieldCheckIcon({ size = 18, className }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  );
}

export function LogOutIcon({ size = 14, className }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  );
}

// ───────────────────────── Internaționalizare (RO/EN) ─────────────────────────
// Același tipar ca Hub Client (frontend/src/App.jsx, §2.C.3): traducere prin
// CĂUTAREA DIRECTĂ a textului românesc sursă, nu chei artificiale — orice
// șir netradus rămâne pur și simplu în română, deci nu produce niciodată
// chei brute vizibile pe ecran. Acoperă interfața aplicației (chrome-ul:
// login, bară de sus, taburi, etichete, butoane) — conținutul introdus de
// utilizator (titluri de caz, termeni Tripod custom, text liber în
// textarea-uri) rămâne netradus, exact ca la Hub Client (nu se poate
// traduce automat text liber scris de altcineva).
const LANG_STORAGE_KEY = "investigatii-lang";
function getLang() {
  try {
    return localStorage.getItem(LANG_STORAGE_KEY) === "en" ? "en" : "ro";
  } catch {
    return "ro";
  }
}
function setStoredLang(lang) {
  try {
    localStorage.setItem(LANG_STORAGE_KEY, lang === "en" ? "en" : "ro");
  } catch {
    // ignore
  }
}
export function useLang() {
  const [lang, setLangState] = useState(getLang());
  const changeLang = (l) => {
    setStoredLang(l);
    setLangState(l === "en" ? "en" : "ro");
  };
  return [lang, changeLang];
}

const EN_STRINGS = {
  "INVESTIGAȚII · SUITA HUB CLIENT": "INVESTIGATIONS · HUB CLIENT SUITE",
  "Managementul cazurilor": "Case management",
  "de investigație": "for investigations",
  "De la primul raport de incident până la analiza Tripod Beta și interviul investigativ — un singur loc pentru toată documentația unui caz.":
    "From the first incident report to the Tripod Beta analysis and the investigative interview — one place for a case's entire documentation.",
  "Made by": "Made by",
  "Autentificare": "Sign in",
  "Parolă": "Password",
  "Se autentifică…": "Signing in…",
  "Intră în platformă": "Enter platform",
  "INVESTIGAȚII": "INVESTIGATIONS",
  "Cazuri": "Cases",
  "Ieșire": "Log out",
  "Se încarcă...": "Loading...",
  "Anulează": "Cancel",
  "+ Caz nou": "+ New case",
  "Nr. dosar": "File #",
  "Titlu": "Title",
  "Client": "Client",
  "Status": "Status",
  "Creat": "Created",
  "Niciun caz momentan.": "No cases yet.",
  "Deschis": "Open",
  "Închis": "Closed",
  "Număr dosar": "File / case number",
  "Titlul incidentului": "Incident title",
  "-Selectează-": "-Select-",
  "Nume client nou": "New client name",
  "Adaugă client": "Add client",
  "Numele subiectului": "Subject name",
  "Numele persoanei sau entității": "Name of person or entity",
  "Identitatea investigatorului": "Investigator identity",
  "Numele investigatorului responsabil": "Lead investigator name",
  "Creează caz": "Create case",
  "1. Date administrative": "1. Admin",
  "2. Termeni de referință": "2. Terms of Reference",
  "3. Introducere": "3. Introduction",
  "4. Rezumat": "4. Summary",
  "5. Plan de investigație": "5. Investigation Plan",
  "6. Cronologie": "6. Storyboard",
  "7. Cauze imediate": "7. Immediate Causes",
  "8. Diagrama de analiză": "8. Analysis Diagram",
  "9. Recomandări": "9. Recommendations",
  "10. Anexe": "10. Enclosures",
  "Interviu (PEACE)": "Interview (PEACE)",
  "Termeni de referință": "Terms of Reference",
  "Introducerea raportului": "Report introduction",
  "Rezumat executiv": "Executive summary",
  "Recomandări": "Recommendations",
  "Se salvează...": "Saving...",
  "Salvează": "Save",
  "Categorie": "Category",
  "Grup indicator": "Indicator Group",
  "Acțiune": "Action",
  "Șterge": "Delete",
  "+ Adaugă rând": "+ Add row",
  "Gestionare categorii": "Category management",
  "Categorie nouă": "New category",
  "Adaugă": "Add",
  "Gestionare indicatori": "Indicator management",
  "-Selectează categoria-": "-Select category-",
  "Indicator nou": "New indicator",
  "Ce trebuie să conțină": "What it should include",
  "Cine a comandat investigația și de ce.": "Who commissioned the investigation and why.",
  "Scopul și obiectivele anchetei — ce anume trebuie stabilit.": "The purpose and objectives of the inquiry — what exactly needs to be established.",
  "Ce este inclus și ce este exclus din anchetă.": "What is included and excluded from the inquiry.",
  "Autoritatea și accesul investigatorului: documente, persoane, sisteme.": "The investigator's authority and access: documents, people, systems.",
  "Termenul limită și resursele alocate.": "The deadline and allocated resources.",
  "Nivelul de confidențialitate al raportului.": "The report's confidentiality level.",
  "Exemplu: „Prezenta investigație este comandată de conducerea companiei ca urmare a [incident]. Scopul este stabilirea cauzei, întinderii pagubelor și responsabilităților. Investigatorul are acces la înregistrările CCTV, documentele de transport și poate intervieva personalul implicat. Ancheta exclude aspectele de natură penală. Termen: 15 zile lucrătoare.”":
    "Example: “This investigation is commissioned by company management following [incident]. Its purpose is to establish the cause, the extent of the losses, and responsibilities. The investigator has access to CCTV footage and transport documents, and may interview the staff involved. The inquiry excludes criminal matters. Deadline: 15 working days.”",
  "Ce s-a întâmplat, când și unde — faptele brute, fără interpretare sau concluzii.": "What happened, when and where — the raw facts, without interpretation or conclusions.",
  "Cum a fost sesizat sau descoperit incidentul.": "How the incident was reported or discovered.",
  "Cine a raportat și către cine.": "Who reported it, and to whom.",
  "Context relevant: incidente similare anterioare, particularități ale sitului.": "Relevant context: similar prior incidents, site-specific particulars.",
  "Exemplu: „În data de [...], la [locație], s-a constatat [...]. Incidentul a fost raportat de [...] către [...]. Aceasta este a doua investigație de acest tip din ultimele 12 luni la acest sit.”":
    "Example: “On [date], at [location], [...] was discovered. The incident was reported by [...] to [...]. This is the second investigation of this kind at this site in the last 12 months.”",
  "Se scrie ultimul, dar se citește primul — sinteza pentru cineva care nu va citi tot raportul.": "Written last, read first — the summary for someone who won't read the full report.",
  "Ce s-a întâmplat, în 2-3 propoziții.": "What happened, in 2-3 sentences.",
  "Cauza principală identificată.": "The main cause identified.",
  "Impactul: valoare, operațional, de imagine.": "The impact: financial value, operational, reputational.",
  "Recomandarea cheie.": "The key recommendation.",
  "Exemplu: „Investigația a stabilit că [...], cauzat de [cauza rădăcină]. Prejudiciul estimat este de [valoare]. Se recomandă [acțiune principală] pentru prevenirea recurenței.”":
    "Example: “The investigation established that [...], caused by [root cause]. The estimated loss is [value]. [Main action] is recommended to prevent recurrence.”",
  "Când": "When",
  "Descrierea evenimentului": "Event description",
  "Descrierea evenimentului...": "Event description...",
  "+ Adaugă": "+ Add",
  "Condiție / Faptă": "Condition / Act",
  "Dovadă / Verificare": "Evidence / Verification",
  "Condiție/Faptă": "Condition/Act",
  "Dovadă/Verificare": "Evidence/Verification",
  "Rădăcină latentă": "Latent Root",
  "Precondiție": "Precondition",
  "Eșec activ": "Active Failure",
  "Barieră eșuată": "Failed Barrier",
  "+ Adaugă traseu": "+ Add path",
  "Gestionare bază de termeni Tripod": "Manage Tripod term database",
  "Termen...": "Term...",
  "Descarcă raport PDF": "Download PDF report",
  "Latent": "Latent",
  "Eșec activ (scurt)": "Active",
  "Barieră": "Barrier",
  "Un singur registru de dovezi pentru tot cazul — fotografii, înregistrări video și documente. Adaugă la descriere unde/când se aplică fiecare dovadă (ex. „cronologie, ora 09:40”), nu e nevoie de un tab separat pentru fiecare capitol.":
    "A single evidence register for the whole case — photos, video recordings and documents. Use the description to note where/when each item applies (e.g. “storyboard entry, 09:40”), no need for a separate tab per chapter.",
  "Tip": "Type",
  "Fișier": "File",
  "Descriere": "Description",
  "Încărcat de": "Uploaded by",
  "Data": "Date",
  "Mărime": "Size",
  "Nicio dovadă încărcată încă.": "No evidence uploaded yet.",
  "Fotografie": "Photo",
  "Descriere / context (opțional)...": "Description / context (optional)...",
  "+ Încarcă dovadă": "+ Upload evidence",
  "Formate acceptate: JPEG, PNG, WEBP, HEIC, MP4, MOV, WEBM, PDF — maximum 100 MB per fișier.":
    "Accepted formats: JPEG, PNG, WEBP, HEIC, MP4, MOV, WEBM, PDF — maximum 100 MB per file.",
  "Interviurile sunt opționale — nu toate investigațiile necesită discuții cu martori sau suspecți. Rolul unei persoane se poate schimba pe parcurs (ex. un martor poate deveni suspect); fiecare sesiune de interviu păstrează rolul avut la acel moment, iar rolul curent de mai jos poate fi actualizat oricând, inclusiv la închiderea cazului, când devine rolul final.":
    "Interviews are optional — not every investigation needs to talk to witnesses or suspects. A person's role can change as the case progresses (e.g. a witness may turn out to be a suspect); each interview session keeps the role they had at that time, while the current role below can be updated at any point, including at case closure, when it becomes the final one.",
  "Nicio persoană intervievată încă.": "No one interviewed yet.",
  "Adaugă persoană intervievată": "Add interviewee",
  "Nume complet": "Full name",
  "Notițe (opțional)...": "Notes (optional)...",
  "+ Adaugă persoană": "+ Add person",
  "Martor": "Witness",
  "Victimă": "Victim",
  "Incriminat": "Accused",
  "+ Adaugă sesiune de interviu": "+ Add interview session",
  "Rol la momentul interviului": "Role at time of interview",
  "Data interviului": "Interview date",
  "Șterge sesiunea": "Delete session",
  "Interacțiune și explicare": "Engage & Explain",
  "Evaluare": "Evaluation",
  "Declarația persoanei": "Subject's statement",
  "Dovadă obiectivă": "Objective evidence",
  "Explicație": "Explanation",
  "Niciun rând încă.": "No rows yet.",
  "SUE = Statement – Undisclosed Evidence – Explanation (Declarație – Dovadă nedezvăluită – Explicație): o tehnică de testare a credibilității. Se notează ce a spus persoana, apoi dovada obiectivă — nedezvăluită persoanei până în acel moment — care confirmă sau contrazice declarația, iar la final explicația oferită de persoană când este confruntată cu acea dovadă. Câte un rând per discrepanță relevantă.":
    "SUE = Statement – Undisclosed Evidence – Explanation: a credibility-testing technique. Record what the person said, then the objective evidence — not yet disclosed to them at that point — that confirms or contradicts the statement, and finally the explanation they give once confronted with that evidence. One row per relevant discrepancy.",
  "11. Închidere": "11. Closure",
  "Închis la": "Closed on",
  "Redeschide investigația": "Reopen investigation",
  "Închide investigația": "Close investigation",
  "Închiderea nu blochează editarea — dacă apar detalii suplimentare ulterior, cazul poate fi redeschis și orice secțiune a raportului poate fi completată în continuare, oricând.":
    "Closing doesn't lock editing — if further details come up later, the case can be reopened and any section of the report can still be completed, at any time.",
  "Concluzia de închidere": "Closure conclusion",
  "Confirmarea constatărilor finale, eventuale mențiuni pentru un cititor care revine ulterior asupra cazului...":
    "Confirmation of the final findings, any notes for a reader who returns to the case later...",
  "Planificare și pregătire": "Planning and Preparation",
  "Relatare, clarificare și contestare": "Account, Clarify, Challenge",
  "Matricea SUE (clarificare și contestare)": "SUE matrix (clarify and challenge)",
  "Încheierea interviului": "Interview closure",
  "Structura PEACE": "The PEACE structure",
  "Obiectivele interviului — ce anume trebuie stabilit prin discuția cu această persoană.":
    "The interview's objectives — what exactly needs to be established through the discussion with this person.",
  "Analiza dovezilor deja disponibile (cronologie, alte declarații, probe fizice) înainte de a intra în discuție.":
    "Reviewing the evidence already available (storyboard, other statements, physical evidence) before going into the discussion.",
  "Rolul avut de persoană la acel moment (martor/suspect/victimă) și procedura corespunzătoare.":
    "The person's role at that time (witness/suspect/victim) and the procedure that goes with it.",
  "Dacă e necesară prezența unui reprezentant legal sau a unui adult însoțitor.":
    "Whether a legal representative or an appropriate adult needs to be present.",
  "Locul, momentul și durata estimată a interviului.": "The interview's location, time, and estimated duration.",
  "Exemplu: „Interviu programat cu șoferul, în calitate de martor la acest stadiu. Pregătite jurnalul GPS și foaia de parcurs pentru verificare încrucișată. Durată estimată: 30 minute, fără reprezentant legal necesar.”":
    "Example: “Interview scheduled with the driver, as a witness at this stage. GPS log and trip sheet prepared for cross-checking. Estimated duration: 30 minutes, no legal representative needed.”",
  "Stabilirea unui raport profesional, fără presiune sau coerciție.": "Building a professional rapport, without pressure or coercion.",
  "Explicarea clară a scopului interviului și a modului în care va decurge.": "Clearly explaining the interview's purpose and how it will proceed.",
  "Comunicarea rolului avut de persoană la acel moment (martor/suspect etc.) și a drepturilor corespunzătoare.":
    "Communicating the person's role at that time (witness/suspect etc.) and their corresponding rights.",
  "Confirmarea că persoana a înțeles și e pregătită să continue.": "Confirming the person has understood and is ready to proceed.",
  "Exemplu: „I s-a explicat că discuția vizează clarificarea traseului din [dată] și că este tratată ca martor la acest stadiu, nu ca suspect.”":
    "Example: “It was explained that the discussion is meant to clarify the route on [date], and that they're being treated as a witness at this stage, not a suspect.”",
  "Persoana își relatează versiunea neîntreruptă, fără sugestii sau întrebări conducătoare.":
    "The person gives their account uninterrupted, without suggestions or leading questions.",
  "Urmează întrebări de clarificare pentru detaliile lipsă sau ambigue.": "Followed by clarifying questions for missing or ambiguous details.",
  "Discrepanțele dintre declarație și dovezile obiective se contestă direct, cu fapte concrete — foloseşte matricea SUE de mai jos (Declarație – Dovadă – Explicație) pentru fiecare discrepanță relevantă.":
    "Discrepancies between the statement and the objective evidence are challenged directly, with concrete facts — use the SUE matrix below (Statement – Evidence – Explanation) for each relevant discrepancy.",
  "Dovezile nu se dezvăluie toate deodată — se introduc treptat, ca să testeze consistența relatării.":
    "Evidence isn't revealed all at once — it's introduced gradually, to test the consistency of the account.",
  "Exemplu: „A declarat că traseul a decurs fără opriri neprogramate. Confruntat cu jurnalul GPS (oprire de 47 min.), a recunoscut oprirea și a oferit o explicație neconfirmată de nicio altă sursă.”":
    "Example: “Stated the route had no unscheduled stops. Confronted with the GPS log (47-min. stop), he admitted the stop and gave an explanation not confirmed by any other source.”",
  "Rezumă principalele puncte ale declarației, cu confirmarea persoanei că sunt corecte.":
    "Summarizing the main points of the statement, with the person confirming they're accurate.",
  "Oferă persoanei ocazia să adauge orice altceva consideră relevant.": "Giving the person a chance to add anything else they consider relevant.",
  "Explică ce urmează în investigație și, dacă e cazul, cum va fi contactată din nou.":
    "Explaining what happens next in the investigation and, if applicable, how they may be contacted again.",
  "Se încheie într-o manieră profesională, indiferent de rezultatul interviului.": "Ending in a professional manner, regardless of how the interview went.",
  "Exemplu: „I s-a rezumat declarația, a confirmat-o ca fiind corectă. I s-a explicat că investigația continuă și că ar putea fi contactat pentru clarificări suplimentare.”":
    "Example: “The statement was summarized back to them and confirmed as accurate. It was explained that the investigation continues and they might be contacted for further clarification.”",
  "Compară informația nouă cu dosarul existent al cazului — ce se confirmă, ce se contrazice.":
    "Comparing the new information against the existing case file — what it confirms, what it contradicts.",
  "Evaluează dacă obiectivele stabilite la planificare au fost atinse.": "Assessing whether the objectives set during planning were met.",
  "Stabilește pașii următori: e nevoie de un nou interviu, de reclasificarea rolului persoanei, de verificări suplimentare?":
    "Determining the next steps: is another interview needed, should the person's role be reclassified, are further checks needed?",
  "Se face după interviu, nu în timpul lui — e o etapă separată de reflecție.": "Done after the interview, not during it — a separate, reflective step.",
  "Exemplu: „Declarația inițială contrazice jurnalul GPS. Discrepanța justifică reclasificarea ca suspect și o a doua sesiune de interviu, cu procedura corespunzătoare noului statut.”":
    "Example: “The initial statement contradicts the GPS log. The discrepancy justifies reclassifying him as a suspect and a second interview session, following the procedure for the new status.”",
  "Cont": "Account",
  "Contul meu": "My account",
  "Schimbă email-ul și/sau parola de autentificare. Confirmă cu parola curentă.":
    "Change your login email and/or password. Confirm with your current password.",
  "Parolă nouă (opțional)": "New password (optional)",
  "Lasă gol dacă nu o schimbi": "Leave blank if not changing it",
  "Confirmă parola nouă": "Confirm new password",
  "Parola curentă (obligatoriu, pentru confirmare)": "Current password (required, for confirmation)",
  "Actualizat cu succes.": "Updated successfully.",
  "Parola nouă și confirmarea nu coincid.": "New password and confirmation don't match.",
  "Nu ai schimbat nimic — completează un email nou și/sau o parolă nouă.":
    "You haven't changed anything — fill in a new email and/or a new password.",
  // Identice ca formă în RO și EN — adăugate explicit oricum (nu doar
  // lăsate să „treacă" nemodificate prin fallback-ul din t()), ca
  // dicționarul să rămână sursa de adevăr completă pentru orice text de
  // interfață, verificabilă printr-un audit static, nu doar vizual.
  "Email": "Email",
  "Video": "Video",
  "Document": "Document",
  "Suspect": "Suspect",
  "Echipă": "Team",
  "+ Cont nou": "+ New account",
  "Nume": "Name",
  "Rol": "Role",
  "Activ": "Active",
  "Dezactivat": "Deactivated",
  "Resetează parola": "Reset password",
  "Dezactivează": "Deactivate",
  "Reactivează": "Reactivate",
  "Niciun cont încă.": "No accounts yet.",
  "Parolă inițială": "Initial password",
  "Minim 10 caractere — comunic-o separat noului cont": "Minimum 10 characters — communicate it separately to the new account",
  "Înrolează cont": "Enroll account",
  "Parolă nouă (minim 10 caractere)": "New password (minimum 10 characters)",
  "Confirmă": "Confirm",
  "Parola trebuie să aibă minim 10 caractere.": "Password must be at least 10 characters.",
  "Nu ai cont?": "Don't have an account?",
  "Creează unul": "Create one",
  "Ai deja cont?": "Already have an account?",
  "Cont nou pentru organizația ta": "New account for your organization",
  "Numele organizației": "Organization name",
  "Numele tău complet": "Your full name",
  "Parolă (minim 10 caractere)": "Password (minimum 10 characters)",
  "Confirmă parola": "Confirm password",
  "Se creează contul…": "Creating account…",
  "Creează organizația": "Create organization",
};

export function t(lang, roText) {
  if (lang !== "en") return roText;
  return EN_STRINGS[roText] || roText;
}

// Comutator RO/EN — aceeași pereche (lang, onLangChange) e ridicată o
// singură dată în App() (persistă în localStorage), ca ecranul de login și
// restul aplicației să rămână mereu în aceeași limbă, inclusiv peste logout.
export function LangSwitch({ lang, onChange }) {
  return (
    <div className="lang-switch">
      <button onClick={() => onChange("ro")} className={lang === "ro" ? "active" : ""}>RO</button>
      <span>/</span>
      <button onClick={() => onChange("en")} className={lang === "en" ? "active" : ""}>EN</button>
    </div>
  );
}

const STATUS_LABELS = { Open: { ro: "Deschis", en: "Open" }, Closed: { ro: "Închis", en: "Closed" } };
export function statusLabel(status, lang) {
  return STATUS_LABELS[status]?.[lang] || status;
}
